import { OidcProvider } from '@axa-fr/react-oidc';
import { type ReactNode, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './styles/global.css';
import App from './App';
import { fetchAuthConfig } from './api/authConfig';
import AuthGate from './auth/AuthGate';
import { setActiveProvider, setAuthConfig, usableProviders } from './auth/config';
import { logOidcEvent } from './auth/debug';
import { forgetProvider, readProvider, rememberProvider } from './auth/lastProvider';
import { navigateAfterCallback } from './auth/navigation';
import { buildOidcConfiguration, createOidcFetch } from './auth/oidc';
import {
  takeAutoSignInFromUrl,
  takeLoginHintFromUrl,
  takeProviderFromUrl,
} from './auth/providerFromUrl';
import RouterBridge from './auth/RouterBridge';
import { attemptSilentSignIn } from './auth/reauth';
import { notifyUnauthorized } from './auth/session';
import {
  decideSignIn,
  goToEmailStep,
  goToProviderScreen,
  signInRootPath,
} from './auth/signInRoute';
import { notifySignedOut, takeSignedOutOnReturn } from './auth/signOut';
import ErrorBoundary, { AppErrorFallback } from './components/ErrorBoundary';
import { EmailSignIn, RedirectingToSignIn, SigningIn } from './components/LoginScreen';
import { ThemeProvider } from './hooks/useTheme';
import type { AuthConfig, AuthProviderConfig } from './types/api';

// Built once. The library reads its fetch on every render, so a fresh object would look
// like a configuration change on each pass.
const oidcFetch = createOidcFetch(window.fetch.bind(window));

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root element not found in index.html');
const root = createRoot(rootEl);

/** The chrome every variant shares, so the trees below differ only where they must. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <StrictMode>
      {/* Outside ThemeProvider and BrowserRouter on purpose, so a throw from either is
          caught too. */}
      <ErrorBoundary label="app" fallback={<AppErrorFallback />}>
        <ThemeProvider>
          <BrowserRouter basename={import.meta.env.BASE_URL}>{children}</BrowserRouter>
        </ThemeProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}

function withSignIn({
  provider,
  providers,
  onUseDifferentProvider = null,
  autoSignIn = false,
  silentResume = false,
  loginHint = null,
  signedOutOnReturn,
}: {
  provider: AuthProviderConfig;
  providers: AuthProviderConfig[];
  onUseDifferentProvider?: (() => void) | null;
  autoSignIn?: boolean;
  silentResume?: boolean;
  loginHint?: string | null;
  signedOutOnReturn: boolean;
}) {
  return (
    <>
      {/* Outside OidcProvider so it is mounted during the callback render, which is
          exactly when the callback needs a router to hand back to. */}
      <RouterBridge />
      <OidcProvider
        configuration={buildOidcConfiguration(provider)}
        getFetch={() => oidcFetch}
        navigateAfterCallback={navigateAfterCallback}
        callbackSuccessComponent={SigningIn}
        // Both of the library's own hand-off screens are unstyled English on our origin.
        // This one is the more visible of the two: it is what a person sees for as long as
        // the redirect to MIKE Cloud takes, and the only screen they see if it never comes.
        authenticatingComponent={RedirectingToSignIn}
        // The two handlers below exist to keep `sessionLostComponent` off the screen, and
        // providing them is what does it: the library renders that component only when
        // the matching handler is absent. It is a dead end, with no control on it at all,
        // and both of these events used to arrive there, though they mean opposite things.
        //
        // A refresh that failed is recoverable, and usually without anything being seen.
        onSessionLost={() => {
          // Falls through to our own sign-in screen when the retry is already spent, so a
          // recovery that cannot work ends on a button rather than in a loop.
          if (!attemptSilentSignIn()) notifyUnauthorized();
        }}
        // A sign-out in another tab is not a fault and must not be undone: this tab says
        // so and stays put.
        onLogoutFromAnotherTab={notifySignedOut}
        // Silent unless `mikeai.debug-auth` is set in localStorage; see auth/debug.ts.
        onEvent={logOidcEvent}
      >
        <AuthGate
          providers={providers}
          onUseDifferentProvider={onUseDifferentProvider}
          autoSignIn={autoSignIn}
          silentResume={silentResume}
          loginHint={loginHint}
          initiallySignedOut={signedOutOnReturn}
        >
          <App />
        </AuthGate>
      </OidcProvider>
    </>
  );
}

/**
 * The design-system gallery at /ui.
 *
 * Suffix-matched rather than compared, because the app is served under a base
 * path in every deployed environment (`/apps/mikeai/` on AdaLab) and the path
 * here is the browser's, not the router's.
 */
function isGalleryPath(): boolean {
  return window.location.pathname.replace(/\/+$/, '').endsWith('/ui');
}

/**
 * Ask the backend how to authenticate, then render accordingly.
 *
 * The sign-in gate used to be unconditional and configured from build args. That made an
 * image built without them into a sign-in screen that could never sign anyone in, and it
 * made the SPA's idea of the auth mode a second setting that had to be kept in step with
 * the backend's `AUTH_PROVIDER` by hand. Those two disagreeing is worse than it sounds:
 * the `local` provider ignores the Authorization header entirely, so a SPA signing people
 * in against a `local` backend resolves everyone to one shared identity and one shared
 * history, while looking like it works.
 *
 * A failed config call renders the error screen rather than the app. Failing closed is
 * the only safe direction: we do not know whether this deployment expects a token, and
 * guessing wrong gives either an app whose every request is refused, or an unauthenticated
 * app that should not be.
 */
async function boot() {
  // Rendered before the auth config is even fetched: the gallery has no backend
  // and no user, and going through the sign-in gate to look at a button would
  // make it useless for anyone without an account on this environment. The
  // `import.meta.env.DEV` guard is what keeps it out of production, and because
  // the import is dynamic and unreachable behind that constant, the bundler
  // drops the whole gallery chunk from a production build rather than shipping
  // it unreferenced.
  if (import.meta.env.DEV && isGalleryPath()) {
    const { default: UIGallery } = await import('./ui/gallery/UIGallery');
    root.render(
      <Shell>
        <UIGallery />
      </Shell>,
    );
    return;
  }

  let config: AuthConfig;
  try {
    config = await fetchAuthConfig();
  } catch (error) {
    console.error('Could not read the auth configuration from the backend.', error);
    root.render(<AppErrorFallback />);
    return;
  }

  setAuthConfig(config);
  const providers = usableProviders(config);
  if (providers.length === 0) {
    root.render(
      <Shell>
        <App />
      </Shell>,
    );
    return;
  }
  renderSignIn(providers);
}

/**
 * Decide which screen this page load shows, then mount it.
 *
 * Resolved here rather than inside the gate because `OidcProvider` is configured for
 * exactly one authority, so it cannot be mounted before the answer is known. The rules are
 * in `signInRoute.ts`, and the short form is that the URL beats storage and a session
 * beats both: `/signin` is the email step whatever is remembered, `/signin/<provider>` is
 * that provider's screen, any other path falls back to the remembered provider and then to
 * the email step, and a browser already signed in goes on into the app from any of them. The
 * remembered answer has to survive the round trip to the identity provider and back to the
 * callback route, which is why it is stored rather than held here.
 *
 * A stored or URL-supplied name that no longer matches a configured provider is ignored
 * rather than trusted, so removing a provider from `AUTH_PROVIDER` falls back to asking
 * instead of rendering a gate against an authority the backend will not validate.
 */
function renderSignIn(providers: AuthProviderConfig[]): void {
  // Each read once. Both consume the parameter as they read it, so calling one inside a
  // predicate ran it per provider and answered only the first one.
  const namedOnUrl = takeProviderFromUrl();
  const autoRequested = takeAutoSignInFromUrl();
  const loginHint = takeLoginHintFromUrl();
  // An in-place sign-out (Entra) leaves through Microsoft and comes back to the app root
  // as a fresh load; this is how the page that comes back knows to say so.
  const signedOutOnReturn = takeSignedOutOnReturn();
  const decision = decideSignIn({
    pathname: window.location.pathname,
    providers,
    namedOnUrl,
    autoRequested,
    remembered: readProvider(),
  });

  if (decision.kind === 'email') {
    // The email step lives at /signin, so that Continue can leave a history entry behind
    // it. A cold start at the app root or a stale provider path is moved there without
    // adding an entry of its own.
    const root_ = signInRootPath();
    if (window.location.pathname !== root_) window.history.replaceState(null, '', root_);
    // With a provider remembered, this browser may hold a session in the service worker,
    // and only that provider's client can find it. So the email step is rendered through
    // the gate, which on this path shows it to anybody who turns out not to be signed in
    // and sends everyone else on into the app.
    //
    // Not signed in but with an account remembered, the gate may also resume silently, as
    // it does on the app paths. The worker's memory does not outlive the browser while the
    // identity provider's own cookie does, so a restart otherwise meant typing an address
    // to trigger the same round trip `/` makes on its own. The email step is then the
    // screen for a browser with nobody remembered, or one that has just signed out, which
    // forgets the account before anything else.
    if (decision.sessionProvider) {
      setActiveProvider(decision.sessionProvider);
      root.render(
        <Shell>
          {withSignIn({
            provider: decision.sessionProvider,
            providers,
            silentResume: true,
            signedOutOnReturn,
          })}
        </Shell>,
      );
      return;
    }
    root.render(
      <Shell>
        <EmailSignIn
          providers={providers}
          onChoose={goToProviderScreen}
          reason={signedOutOnReturn ? 'signed-out' : 'new'}
        />
      </Shell>,
    );
    return;
  }

  const { provider: chosen, autoSignIn, silentResume } = decision;
  // Arriving at a provider's screen, by any route, is the choice worth remembering.
  if (providers.length > 1) rememberProvider(chosen.name);
  setActiveProvider(chosen);
  // Offered only when there is another way in. It forgets the stored answer and goes to
  // the email step by a page load, so the provider screen stays in history behind it.
  const useDifferentProvider =
    providers.length > 1
      ? () => {
          forgetProvider();
          goToEmailStep();
        }
      : null;
  root.render(
    <Shell>
      {withSignIn({
        provider: chosen,
        providers,
        onUseDifferentProvider: useDifferentProvider,
        autoSignIn,
        silentResume,
        loginHint,
        signedOutOnReturn,
      })}
    </Shell>,
  );
}

void boot();
